import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { datesHelper } from "@/shared/utils";
import { wsdotWeatherInformationApiMeta } from "../apiMeta";
import {
  type SearchWeatherInformationInput,
  searchWeatherInformationInputSchema,
} from "./shared/weatherInfo.input";
import {
  type WeatherInfo,
  weatherInfoSchema,
} from "./shared/weatherInfo.output";

/**
 * Metadata for the searchWeatherInformation endpoint
 */
export const searchWeatherInformationMeta = {
  functionName: "searchWeatherInformation",
  endpoint:
    "/SearchWeatherInformationAsJson?StationID={StationID}&SearchStartTime={SearchStartTime}&SearchEndTime={SearchEndTime}",
  inputSchema: searchWeatherInformationInputSchema,
  outputSchema: weatherInfoSchema.array(),
  sampleParams: {
    StationID: 1980,
    SearchStartTime: new Date(
      `${datesHelper.yesterday()}T00:00:00Z`
    ).toISOString(),
    SearchEndTime: new Date(`${datesHelper.today()}T23:59:59Z`).toISOString(),
  },
  endpointDescription:
    "Search historical weather information for a station within a time range.",
} satisfies EndpointMeta<SearchWeatherInformationInput, WeatherInfo[]>;

/**
 * Fetch function for retrieving historical weather information for a station within a time range
 */
const { fetch, useHook } = createFetchAndHook<
  SearchWeatherInformationInput,
  WeatherInfo[]
>({
  api: wsdotWeatherInformationApiMeta,
  endpoint: searchWeatherInformationMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/weatherInfo.endpoints").weatherInfoGroup.cacheStrategy,
});

/**
 * Fetch function for retrieving historical weather information for a station within a time range
 */
export const searchWeatherInformation: FetchFactory<
  SearchWeatherInformationInput,
  WeatherInfo[]
> = fetch;

/**
 * React Query hook for retrieving historical weather information for a station within a time range
 */
export const useSearchWeatherInformation: HookFactory<
  SearchWeatherInformationInput,
  WeatherInfo[]
> = useHook;
